import type { ErrorComponentProps } from '@tanstack/react-router';

import { Button } from '@connected/ui/components/button';
import { useEffect } from 'react';

import { logger } from '@/lib/logger';

/**
 * A fallback component to display when a React error boundary catches an error.
 * Uses Tailwind CSS for styling.
 *
 * @param {Error} error - The error object caught by the error boundary.
 * @param {object} [error.info] - Optional object containing additional info, like the component stack trace.
 * @param {string} [error.info.componentStack] - The component stack trace.
 * @param {Function} error.reset - A function to call to attempt resetting the error state (e.g., retry rendering).
 */
export function ErrorFallback({ error, info, reset }: ErrorComponentProps) {
    useEffect(() => {
        logger.error(['ErrorBoundary caught an error: ', error, info], {
            appUrl: window.location.href,
        });
    }, [error, info]);

    return (
        <div
            role="alert"
            className="m-4 rounded-md border border-red-500 bg-background p-4 text-red-500 shadow-md"
        >
            <h2 className="mb-2 text-lg font-semibold">
                Oops! Something went wrong.
            </h2>
            <p className="mb-4 text-sm">
                We encountered an unexpected error. Please try again.
            </p>

            {/* Display Error Message */}
            <div className="mb-4 rounded border border-background-quaternary bg-background-tertiary p-3">
                <p className="font-mono text-sm font-medium">
                    Error:
                    {' '}
                    {error.message}
                </p>
            </div>

            {/* Display Component Stack (if available) */}
            {info?.componentStack && (
                <details className="mb-4 cursor-pointer rounded border border-gray-300 bg-gray-50 p-3">
                    <summary className="text-sm font-medium text-gray-700">
                        Component Stack Trace (Click to expand)
                    </summary>
                    <pre className="mt-2 overflow-auto whitespace-pre-wrap break-words rounded bg-gray-100 p-2 text-xs text-gray-600">
                        {info.componentStack}
                    </pre>
                </details>
            )}

            {/* Reset Button */}
            <Button variant="destructive" onClick={reset}>
                Try Again
            </Button>
        </div>
    );
}
